import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/im1.avif';
import { FiCheckCircle, FiShield, FiSettings, FiTrendingUp, FiAward, FiZap } from 'react-icons/fi';
import pressureswingValves from '../assets/swing/pressureswing_valves.jpeg';
import swing1 from '../assets/swing/swing1.PNG';
import swing2 from '../assets/swing/pscvdesign.PNG';
import swingMatrix from '../assets/swing/swing_matrix.png';
import '../css/swingCheckValves.css';

const designFeatures = [
  {
    title: 'Full-Bore Design',
    description: 'Swing check valves feature a full-bore design that provides unrestricted flow with minimal pressure drop, ensuring optimal system efficiency.'
  },
  {
    title: 'Tilting Disc Mechanism',
    description: 'Advanced tilting disc mechanism allows for rapid response to flow changes, preventing water hammer and ensuring reliable backflow prevention.'
  },
  {
    title: 'Metal-to-Metal Sealing',
    description: 'Premium metal-to-metal seating provides excellent leak-tight performance, suitable for high-pressure and critical service applications.'
  },
  {
    title: 'Compact Construction',
    description: 'Compact design makes swing check valves ideal for space-constrained installations while maintaining excellent flow characteristics.'
  }
];

const technicalSpecs = [
  {
    icon: <FiSettings />,
    label: 'Size Range',
    value: '2" – 48"',
    detail: 'Available in standard and large diameter sizes'
  },
  {
    icon: <FiTrendingUp />,
    label: 'Pressure Rating',
    value: 'Class 150 – 2500',
    detail: 'Suitable for various pressure applications'
  },
  {
    icon: <FiShield />,
    label: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Duplex',
    detail: 'Multiple material options for different applications'
  },
  {
    icon: <FiAward />,
    label: 'Standards',
    value: 'API 594, ASME B16.34',
    detail: 'Compliant with industry standards'
  }
];

const valveConfigurations = [
  {
    name: 'Wafer Type Swing Check Valves',
    description: 'Lightweight wafer-type design installed between two flanges, ideal for space-saving applications.',
    benefits: ['Compact installation', 'Lightweight construction', 'Cost-effective', 'Easy maintenance']
  },
  {
    name: 'Flanged Swing Check Valves',
    description: 'Standard flanged design with bolted connections, providing secure installation and easy access.',
    benefits: ['Robust construction', 'Easy installation', 'Secure sealing', 'Standard connections']
  },
  {
    name: 'High-Pressure Swing Check Valves',
    description: 'Heavy-duty design engineered for high-pressure applications requiring superior performance.',
    benefits: ['High-pressure capability', 'Enhanced durability', 'Reliable operation', 'Robust construction']
  },
  {
    name: 'Silent Check Valves',
    description: 'Specialized design with spring-assisted closing mechanism to prevent water hammer and noise.',
    benefits: ['Silent operation', 'Water hammer prevention', 'Smooth closing', 'Reduced noise']
  }
];

const advantages = [
  {
    icon: <FiZap />,
    title: 'Rapid Response',
    description: 'Swing check valves provide fast response to flow direction changes, quickly closing to prevent backflow and protect system integrity.'
  },
  {
    icon: <FiShield />,
    title: 'Reliable Backflow Prevention',
    description: 'Effective backflow prevention protects pumps, compressors, and other equipment from reverse flow damage, ensuring system safety.'
  },
  {
    icon: <FiSettings />,
    title: 'Low Maintenance',
    description: 'Simple design with minimal moving parts results in low maintenance requirements and extended service life in industrial applications.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'High Flow Efficiency',
    description: 'Full-bore design and tilting disc mechanism provide excellent flow characteristics with minimal pressure drop across the valve.'
  }
];

const applications = [
  {
    title: 'Pump Discharge Lines',
    description: 'Protecting pumps from reverse flow and ensuring unidirectional flow in discharge systems.'
  },
  {
    title: 'Water Distribution Systems',
    description: 'Municipal water systems and distribution networks requiring reliable backflow prevention.'
  },
  {
    title: 'Steam Systems',
    description: 'Steam lines and condensate return systems where backflow prevention is critical.'
  },
  {
    title: 'Oil & Gas Pipelines',
    description: 'Pipeline applications requiring reliable check valve operation in transmission systems.'
  },
  {
    title: 'Wastewater Treatment',
    description: 'Wastewater treatment plants where backflow prevention protects treatment processes.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical plants handling corrosive fluids where reliable check valve operation is essential.'
  }
];

const SwingCheckValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scv-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Use setTimeout to ensure refs are set
    const timeoutId = setTimeout(() => {
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.observe(section);
        }
      });
    }, 100); // Small delay to ensure refs are populated

    return () => {
      clearTimeout(timeoutId); // Clear timeout on unmount
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Banner Section */}
      <section className="scv-hero">
        <div className="scv-hero-background">
          <img
            src={banner}
            alt="Swing Check Valves Banner"
            className="scv-hero-image"
          />
          <div className="scv-hero-overlay"></div>
        </div>
        <div className="scv-hero-content">
          <h1 className="scv-hero-title">Swing Check Valves</h1>
          <p className="scv-hero-subtitle">
            Reliable backflow prevention valves designed for efficient unidirectional flow control
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scv-section scv-overview initial-visible"
      >
        <div className="scv-container">
          <div className="scv-intro-content">
            <div className="scv-intro-image">
              <img src={pressureswingValves} alt="Swing Check Valves" className="scv-intro-img" />
            </div>
            <div className="scv-intro-description">
              <h2>Swing Check Valves</h2>
              <p>
                The Swing Check Valve is used to prevent back flow in the line. It's the most
                used one in the industry. The most used standards of reference are API 6D, regular
                opening, API 594, BS 1868. Constructions with internal hinge pins are available,
                together with the more traditional construction with hinge pins inserted from the side.
                The latter allows application of optional requests, such as position indicators (with or
                without limit switches), locking systems in the open position (to exclude the
                unidirectional non return function), and anti-slam external counter weights. A rotary
                anti-slam system applied to the external part of the hinge pin is also available (the
                industry calls it a "dash pot system"). Materials available include carbon steel, low
                temperature carbon steel, low alloy steel, austenitic stainless steel, austenitic-ferritic
                duplex stainless steel, and nickel alloys.
              </p>
            </div>
          </div>
          <div className="scv-specs-row">
            <div className="scv-spec-card-horizontal">
              <h3>Product Range</h3>
              <ul>
                <li>Piping component size : 2" ~ 24" (DN50-DN600)</li>
                <li>Pressure : ANSI Class 150~2500</li>
                <li>Temperature : -196℃~ 500℃</li>
                <li>Body Materials : Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                <li>Trim (Disc/Stem) : Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
              </ul>
            </div>
            <div className="scv-spec-card-horizontal">
              <h3>Technical Specifications</h3>
              <ul>
                <li>Design : ASME 16.34/ API 6D/ BS 1868 / API 594</li>
                <li>Face to Face : ASME B16.10</li>
                <li>End Flange : ASME B16.5</li>
                <li>BW End : ASME B16.25</li>
                <li>Inspection and Testing : API 598/API 6D/ ISO 12266-1</li>
                <li>Special : NACE MR-01-75, IBR</li>
              </ul>
            </div>
            <div className="scv-spec-card-horizontal">
              <h3>Design Features</h3>
              <ul>
                <li>Bolted Cover</li>
                <li>Swing Type Disc</li>
                <li>6" & larger with Eye Bolt for lifting</li>
                <li>3" and above with drain plug arrangement</li>
                <li>Confined Bonnet Gasket</li>
                <li>Flanged, Butt Weld End connections</li>
                <li>Dash pot/Counter weight arrangement</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Product Availability Matrix Section */}
      <div className="scv-specification-table-section">
        <div className="scv-container">
          <h2 className="scv-table-title">Product Availability Matrix</h2>
          <img src={swingMatrix} alt="Product Availability Matrix" />
          <p className="scv-legend">Legends : BB - Bolted Bonnet, BC - Bolted Cover, PS - Pressure Seal</p>
        </div>
      </div>

      {/* Materials of Construction Section */}
      <div 
        ref={(el) => (sectionsRef.current[1] = el)}
        className="scv-section scv-material-section initial-visible"
      >
        <div className="scv-container">
          <h2 className="scv-table-title">Materials of Construction</h2>
          <div className="scv-material-content">
            <div className="scv-material-image">
              <img src={swing1} alt="Swing Check Valve Parts" />
            </div>
            <div className="scv-material-table-container">
              <table className="scv-material-table">
                <thead>
                  <tr>
                    <th>PART NO</th>
                    <th>PART</th>
                    <th>MATERIAL OPTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>BODY</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>BONNET</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>SEAT RING</td>
                    <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT).</td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>PLUG</td>
                    <td>ASTM - A 216 WCB + CRA OVERLAY / A351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT.</td>
                  </tr>
                  <tr>
                    <td>5</td>
                    <td>YOKE</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>6</td>
                    <td>THRUST PAD</td>
                    <td>A 479 SS 316 / A 479 SS 316L.</td>
                  </tr>
                  <tr>
                    <td>7</td>
                    <td>SPLIT WASHER</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>8</td>
                    <td>PLUG NUT</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>9</td>
                    <td>STEM</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>10</td>
                    <td>PRESSURE SEAL GASKET</td>
                    <td>GRAPHITE / DIE MOULDED GRAPHITE.</td>
                  </tr>
                  <tr>
                    <td>11</td>
                    <td>SPACER RING</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>12</td>
                    <td>SEGMENTAL RING</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>13</td>
                    <td>FACE RING</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>14</td>
                    <td>LOCKING RING</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>15</td>
                    <td>BACK SEAT BUSH</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>16</td>
                    <td>GLAND PACKING</td>
                    <td>DIE MOULDED GRAPHOIL / FLEXIBLE GRAPHITE WITH SACRIFICIAL CORROSION INHIBITOR AND INCONEL WIRE REINFORCEMENT / TOP & BOTTOM BRAIDED GRAPHITE.</td>
                  </tr>
                  <tr>
                    <td>17</td>
                    <td>PROTECTIVE RING</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>18</td>
                    <td>GLAND BUSH</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                  </tr>
                  <tr>
                    <td>19</td>
                    <td>GLAND FLANGE</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>20</td>
                    <td>STEM GUIDE</td>
                    <td>ASTM A106 Gr.B / SS 304 / SS 316.</td>
                  </tr>
                  <tr>
                    <td>21</td>
                    <td>GUIDE KEY</td>
                    <td>EN8.</td>
                  </tr>
                  <tr>
                    <td>22</td>
                    <td>GEAR BOX</td>
                    <td>COMMERCIAL.</td>
                  </tr>
                  <tr>
                    <td>23</td>
                    <td>YOKE STUDS / NUTS</td>
                    <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                  </tr>
                  <tr>
                    <td>24</td>
                    <td>GLAND STUD / NUTS</td>
                    <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                  </tr>
                  <tr>
                    <td>25</td>
                    <td>GEAR BOLTS</td>
                    <td>HIGH TENSILE.</td>
                  </tr>
                </tbody>
              </table>
              <p className="scv-material-note">Note: Materials not listed above can be offered on request.</p>
            </div>
          </div>

          {/* Dimensional Details Tables */}
          <div className="scv-dimensional-details-section">
            {/* 150 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">SWING CHECK VALVE DIMENSIONAL DETAILS FOR 150 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>51</td><td>203</td><td>203</td><td>216</td><td>152</td><td>152.4</td><td>19.1</td><td>4</td><td>91.9</td><td>14.2</td><td>1.5</td><td>15</td></tr>
                    <tr><td>3" (080)</td><td>77</td><td>241</td><td>241</td><td>254</td><td>190</td><td>152.4</td><td>19.1</td><td>4</td><td>127</td><td>17.5</td><td>1.5</td><td>28</td></tr>
                    <tr><td>4" (100)</td><td>102</td><td>292</td><td>292</td><td>305</td><td>229</td><td>190.5</td><td>19.1</td><td>8</td><td>157.2</td><td>22.4</td><td>1.5</td><td>42</td></tr>
                    <tr><td>6" (150)</td><td>153</td><td>406</td><td>406</td><td>419</td><td>279</td><td>241.3</td><td>22.2</td><td>8</td><td>215.9</td><td>23.9</td><td>1.5</td><td>79</td></tr>
                    <tr><td>8" (200)</td><td>204</td><td>495</td><td>495</td><td>508</td><td>343</td><td>298.4</td><td>22.2</td><td>8</td><td>269.7</td><td>26.9</td><td>1.5</td><td>131</td></tr>
                    <tr><td>10" (250)</td><td>254</td><td>622</td><td>622</td><td>635</td><td>406</td><td>362</td><td>25.4</td><td>12</td><td>323.9</td><td>28.4</td><td>1.5</td><td>177</td></tr>
                    <tr><td>12" (300)</td><td>305</td><td>698</td><td>698</td><td>711</td><td>483</td><td>431.8</td><td>25.4</td><td>12</td><td>381</td><td>30.2</td><td>1.5</td><td>282</td></tr>
                    <tr><td>14" (350)</td><td>337</td><td>787</td><td>787</td><td>800</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>380</td></tr>
                    <tr><td>16" (400)</td><td>388</td><td>914</td><td>914</td><td>927</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>542</td></tr>
                    <tr><td>18" (450)</td><td>439</td><td>978</td><td>978</td><td>991</td><td>635</td><td>577.8</td><td>31.8</td><td>16</td><td>533.4</td><td>38.1</td><td>1.5</td><td>632</td></tr>
                    <tr><td>20" (500)</td><td>489</td><td>978</td><td>978</td><td>991</td><td>693</td><td>635</td><td>31.8</td><td>20</td><td>584.2</td><td>41.1</td><td>1.5</td><td>855</td></tr>
                    <tr><td>24" (600)</td><td>591</td><td>1295</td><td>1295</td><td>1308</td><td>813</td><td>749.3</td><td>35.1</td><td>20</td><td>692.2</td><td>46</td><td>1.5</td><td>970</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 300 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">SWING CHECK VALVE DIMENSIONAL DETAILS FOR 300 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>51</td><td>267</td><td>267</td><td>283</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>20.6</td><td>1.5</td><td>20</td></tr>
                    <tr><td>3" (080)</td><td>77</td><td>318</td><td>318</td><td>334</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>26.9</td><td>1.5</td><td>40</td></tr>
                    <tr><td>4" (100)</td><td>102</td><td>356</td><td>356</td><td>372</td><td>254</td><td>200.2</td><td>22.2</td><td>8</td><td>157.2</td><td>30.2</td><td>1.5</td><td>65</td></tr>
                    <tr><td>6" (150)</td><td>153</td><td>444</td><td>444</td><td>460</td><td>318</td><td>269.7</td><td>22.2</td><td>12</td><td>215.9</td><td>35.1</td><td>1.5</td><td>118</td></tr>
                    <tr><td>8" (200)</td><td>204</td><td>533</td><td>533</td><td>549</td><td>381</td><td>330.2</td><td>25.4</td><td>12</td><td>269.7</td><td>39.6</td><td>1.5</td><td>193</td></tr>
                    <tr><td>10" (250)</td><td>254</td><td>622</td><td>622</td><td>638</td><td>444</td><td>387.4</td><td>28.6</td><td>16</td><td>323.9</td><td>46</td><td>1.5</td><td>310</td></tr>
                    <tr><td>12" (300)</td><td>305</td><td>711</td><td>711</td><td>727</td><td>521</td><td>450.8</td><td>31.8</td><td>16</td><td>381</td><td>30.2</td><td>1.5</td><td>450</td></tr>
                    <tr><td>14" (350)</td><td>337</td><td>838</td><td>838</td><td>854</td><td>584</td><td>514.4</td><td>31.8</td><td>20</td><td>412.8</td><td>52.3</td><td>1.5</td><td>595</td></tr>
                    <tr><td>16" (400)</td><td>388</td><td>864</td><td>864</td><td>880</td><td>648</td><td>571.5</td><td>35.1</td><td>20</td><td>469.9</td><td>55.6</td><td>1.5</td><td>840</td></tr>
                    <tr><td>18" (450)</td><td>432</td><td>978</td><td>978</td><td>994</td><td>711</td><td>628.6</td><td>35.1</td><td>24</td><td>533.4</td><td>58.7</td><td>1.5</td><td>933</td></tr>
                    <tr><td>20" (500)</td><td>483</td><td>1016</td><td>1016</td><td>1035</td><td>775</td><td>685.8</td><td>35.1</td><td>24</td><td>584.2</td><td>62</td><td>1.5</td><td>1320</td></tr>
                    <tr><td>24" (600)</td><td>583</td><td>1346</td><td>1346</td><td>1368</td><td>914</td><td>812.8</td><td>41.1</td><td>24</td><td>692.2</td><td>68.3</td><td>1.5</td><td>1848</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 600 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">SWING CHECK VALVE DIMENSIONAL DETAILS FOR 600 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>51</td><td>292</td><td>292</td><td>295</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>25.4</td><td>6.4</td><td>30</td></tr>
                    <tr><td>3" (080)</td><td>77</td><td>356</td><td>356</td><td>359</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>31.8</td><td>6.4</td><td>55</td></tr>
                    <tr><td>4" (100)</td><td>102</td><td>432</td><td>432</td><td>435</td><td>273</td><td>215.9</td><td>25.4</td><td>8</td><td>157.2</td><td>38.1</td><td>6.4</td><td>93</td></tr>
                    <tr><td>6" (150)</td><td>153</td><td>559</td><td>559</td><td>562</td><td>356</td><td>292.1</td><td>28.6</td><td>12</td><td>215.9</td><td>47.8</td><td>6.4</td><td>208</td></tr>
                    <tr><td>8" (200)</td><td>200</td><td>660</td><td>660</td><td>663</td><td>419</td><td>349.2</td><td>31.8</td><td>12</td><td>269.7</td><td>55.6</td><td>6.4</td><td>339</td></tr>
                    <tr><td>10" (250)</td><td>254</td><td>787</td><td>787</td><td>790</td><td>508</td><td>431.8</td><td>35.1</td><td>16</td><td>323.9</td><td>63.5</td><td>6.4</td><td>547</td></tr>
                    <tr><td>12" (300)</td><td>305</td><td>838</td><td>838</td><td>841</td><td>559</td><td>489</td><td>35.1</td><td>20</td><td>381</td><td>66.5</td><td>6.4</td><td>715</td></tr>
                    <tr><td>14" (350)</td><td>327</td><td>889</td><td>889</td><td>892</td><td>603</td><td>527</td><td>38.1</td><td>20</td><td>412.8</td><td>69.8</td><td>6.4</td><td>885</td></tr>
                    <tr><td>16" (400)</td><td>375</td><td>991</td><td>991</td><td>994</td><td>686</td><td>603.2</td><td>41.1</td><td>20</td><td>469.9</td><td>76.2</td><td>6.4</td><td>1310</td></tr>
                    <tr><td>18" (450)</td><td>420</td><td>1092</td><td>1092</td><td>1095</td><td>743</td><td>654</td><td>44.5</td><td>20</td><td>533.4</td><td>82.6</td><td>6.4</td><td>1620</td></tr>
                    <tr><td>20" (500)</td><td>464</td><td>1194</td><td>1194</td><td>1200</td><td>813</td><td>723.9</td><td>44.5</td><td>24</td><td>584.2</td><td>88.9</td><td>6.4</td><td>2120</td></tr>
                    <tr><td>24" (600)</td><td>559</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>3100</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PSSCV MOC Section */}
      <div 
        ref={(el) => (sectionsRef.current[2] = el)}
        className="scv-section scv-psscv-moc-section initial-visible"
      >
        <div className="scv-container">
          <h2 className="scv-table-title">PSSCV MOC</h2>
          <div className="scv-psscv-content">
            <div className="scv-psscv-image">
              <img src={swing2} alt="Pressure Seal Swing Check Valve Parts" />
            </div>
            <div className="scv-psscv-table-container">
              <table className="scv-material-table">
                <thead>
                  <tr>
                    <th>PART NO</th>
                    <th>PART</th>
                    <th>MATERIAL OPTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>BODY</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>COVER</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>DISC</td>
                    <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT)</td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>HINGE</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>5</td>
                    <td>BRACKET</td>
                    <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                  </tr>
                  <tr>
                    <td>6</td>
                    <td>HINGE PIN</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718)</td>
                  </tr>
                  <tr>
                    <td>7</td>
                    <td>PRESSURE SEAL GASKET</td>
                    <td>GRAPHITE / DIE MOULDED GRAPHITE</td>
                  </tr>
                  <tr>
                    <td>8</td>
                    <td>DISC NUT</td>
                    <td>ASTM A194 GR. 8, 8M</td>
                  </tr>
                  <tr>
                    <td>9</td>
                    <td>SPLIT PIN</td>
                    <td>STAINLESS STEEL</td>
                  </tr>
                  <tr>
                    <td>10</td>
                    <td>COVER STUD</td>
                    <td>ASTM - A 320 Gr.L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M,</td>
                  </tr>
                  <tr>
                    <td>11</td>
                    <td>COVER NUT</td>
                    <td>ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M</td>
                  </tr>
                  <tr>
                    <td>12</td>
                    <td>BRACKET BOLT</td>
                    <td>ASTM A193 Gr.B8/B8M</td>
                  </tr>
                  <tr>
                    <td>13</td>
                    <td>SEAT RING</td>
                    <td>ASTM - A 216 WCB + CRA OVERLAY / A351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT</td>
                  </tr>
                  <tr>
                    <td>14</td>
                    <td>LIFTING EYE BOLT</td>
                    <td>ASTM A105 / SS 304</td>
                  </tr>
                  <tr>
                    <td>15</td>
                    <td>SEGMENTAL RING</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 479 SS 410 / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718)</td>
                  </tr>
                  <tr>
                    <td>16</td>
                    <td>PROTECTIVE RING</td>
                    <td>A 479 SS 316 / A 479 SS 316L / A 479 SS 410 / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718)</td>
                  </tr>
                </tbody>
              </table>
              <p className="scv-material-note">Note: Materials not listed above can be offered on request.</p>
            </div>
          </div>

          {/* Pressure Seal Dimensional Details Tables */}
          <div className="scv-dimensional-details-section">
            {/* 900 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">PRESSURE SEAL SWING CHECK VALVE DIMENSIONAL DETAILS FOR 900 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>42</td></tr>
                    <tr><td>3" (080)</td><td>73</td><td>381</td><td>381</td><td>384</td><td>241</td><td>190.5</td><td>25.4</td><td>8</td><td>127</td><td>38.1</td><td>6.4</td><td>58</td></tr>
                    <tr><td>4" (100)</td><td>99</td><td>457</td><td>457</td><td>460</td><td>292</td><td>235</td><td>31.8</td><td>8</td><td>157.2</td><td>44.5</td><td>6.4</td><td>110</td></tr>
                    <tr><td>6" (150)</td><td>147</td><td>610</td><td>610</td><td>613</td><td>381</td><td>317.5</td><td>31.8</td><td>12</td><td>215.9</td><td>55.6</td><td>6.4</td><td>242</td></tr>
                    <tr><td>8" (200)</td><td>191</td><td>737</td><td>737</td><td>740</td><td>470</td><td>393.7</td><td>38.1</td><td>12</td><td>269.7</td><td>63.5</td><td>6.4</td><td>599</td></tr>
                    <tr><td>10" (250)</td><td>238</td><td>838</td><td>838</td><td>841</td><td>546</td><td>469.9</td><td>38.1</td><td>16</td><td>323.9</td><td>69.8</td><td>6.4</td><td>893</td></tr>
                    <tr><td>12" (300)</td><td>283</td><td>965</td><td>965</td><td>968</td><td>610</td><td>533.4</td><td>38.1</td><td>20</td><td>381</td><td>79.2</td><td>6.4</td><td>1365</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 1500 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">PRESSURE SEAL SWING CHECK VALVE DIMENSIONAL DETAILS FOR 1500 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>42</td></tr>
                    <tr><td>3" (080)</td><td>70</td><td>470</td><td>470</td><td>473</td><td>267</td><td>203.2</td><td>31.8</td><td>8</td><td>127</td><td>47.8</td><td>6.4</td><td>105</td></tr>
                    <tr><td>4" (100)</td><td>92</td><td>546</td><td>546</td><td>549</td><td>311</td><td>241.3</td><td>35.1</td><td>8</td><td>157.2</td><td>53.8</td><td>6.4</td><td>147</td></tr>
                    <tr><td>6" (150)</td><td>137</td><td>705</td><td>705</td><td>711</td><td>394</td><td>317.5</td><td>38.1</td><td>12</td><td>215.9</td><td>82.6</td><td>6.4</td><td>360</td></tr>
                    <tr><td>8" (200)</td><td>178</td><td>832</td><td>832</td><td>842</td><td>483</td><td>393.7</td><td>41.1</td><td>12</td><td>269.7</td><td>91.9</td><td>6.4</td><td>641</td></tr>
                    <tr><td>10" (250)</td><td>223</td><td>991</td><td>991</td><td>1001</td><td>584</td><td>482.6</td><td>50.8</td><td>12</td><td>323.9</td><td>108</td><td>6.4</td><td>1103</td></tr>
                    <tr><td>12" (300)</td><td>264</td><td>1130</td><td>1130</td><td>1146</td><td>673</td><td>571.5</td><td>53.9</td><td>16</td><td>381</td><td>124</td><td>6.4</td><td>1628</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2500 Class Table */}
            <div className="scv-dimensional-table-wrapper">
              <h3 className="scv-dimensional-title">PRESSURE SEAL SWING CHECK VALVE DIMENSIONAL DETAILS FOR 2500 CLASS</h3>
              <div className="scv-table-scroll-container">
                <table className="scv-dimensional-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>ØB</th>
                      <th colSpan="3">L</th>
                      <th>ØD</th>
                      <th>ØPCD</th>
                      <th>ØH</th>
                      <th>N</th>
                      <th>ØE</th>
                      <th>T</th>
                      <th>RF</th>
                      <th>Weight Kg</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>RF</th>
                      <th>BWE</th>
                      <th>RTJ</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2" (050)</td><td>39</td><td>451</td><td>451</td><td>454</td><td>235</td><td>171.4</td><td>28.6</td><td>8</td><td>91.9</td><td>50.8</td><td>6.4</td><td>89</td></tr>
                    <tr><td>3" (080)</td><td>58</td><td>578</td><td>578</td><td>584</td><td>305</td><td>228.6</td><td>35.1</td><td>8</td><td>127</td><td>66.5</td><td>6.4</td><td>163</td></tr>
                    <tr><td>4" (100)</td><td>73</td><td>673</td><td>673</td><td>683</td><td>356</td><td>273</td><td>41.3</td><td>8</td><td>157.2</td><td>76.2</td><td>6.4</td><td>299</td></tr>
                    <tr><td>6" (150)</td><td>111</td><td>914</td><td>914</td><td>927</td><td>483</td><td>368.3</td><td>53.9</td><td>8</td><td>215.9</td><td>108.0</td><td>6.4</td><td>588</td></tr>
                    <tr><td>8" (200)</td><td>147</td><td>1022</td><td>1022</td><td>1038</td><td>552</td><td>438.2</td><td>53.9</td><td>12</td><td>269.7</td><td>127.0</td><td>6.4</td><td>1071</td></tr>
                    <tr><td>10" (250)</td><td>185</td><td>1270</td><td>1270</td><td>1292</td><td>673</td><td>539.8</td><td>66.7</td><td>12</td><td>323.9</td><td>165.1</td><td>6.4</td><td>1676</td></tr>
                    <tr><td>12" (300)</td><td>219</td><td>1422</td><td>1422</td><td>1444</td><td>762</td><td>619.3</td><td>73</td><td>12</td><td>381</td><td>184.2</td><td>6.4</td><td>2772</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Specifications Section */}
      

      {/* Design Features Section */}
     
      {/* Valve Configurations Section */}
      
      
      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default SwingCheckValves;










